import type { CanvasObject } from "@refinement-canvas/domain";

export const CANVAS_CLIPBOARD_MIME = "application/x-refinement-canvas-objects";
const FALLBACK_PREFIX = "refinement-canvas-objects:";

interface ClipboardPayload {
  readonly version: 1;
  readonly objects: readonly CanvasObject[];
}

export function writeCanvasObjectsToClipboardData(
  clipboardData: DataTransfer,
  objects: readonly CanvasObject[]
): void {
  const payload = JSON.stringify({ version: 1, objects } satisfies ClipboardPayload);
  clipboardData.setData(CANVAS_CLIPBOARD_MIME, payload);
  clipboardData.setData("text/plain", `${FALLBACK_PREFIX}${payload}`);
}

export function readCanvasObjectsFromClipboardData(
  clipboardData: DataTransfer
): readonly CanvasObject[] {
  const custom = clipboardData.getData(CANVAS_CLIPBOARD_MIME);
  if (custom) return parsePayload(custom);
  const text = clipboardData.getData("text/plain");
  if (text.startsWith(FALLBACK_PREFIX)) {
    return parsePayload(text.slice(FALLBACK_PREFIX.length));
  }
  return [];
}

function parsePayload(raw: string): readonly CanvasObject[] {
  try {
    const parsed = JSON.parse(raw) as Partial<ClipboardPayload>;
    if (parsed.version !== 1 || !Array.isArray(parsed.objects)) return [];
    return parsed.objects.filter(isCanvasObject);
  } catch {
    return [];
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isOptionalFiniteNumber(value: unknown): value is number | undefined {
  return value === undefined || isFiniteNumber(value);
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === "string";
}

// text / sticky の文字寄せ用の検証。サーバ側 zod（textAlign: enum(left/center/right)）と同等の
// 制約をクライアント貼り付け経路でも適用し、細工した貼り付けデータで不正値が一時的に
// ローカル状態へ残らないようにする（color / fontSize と同水準に揃える）。
function isOptionalTextAlign(value: unknown): boolean {
  return value === undefined || value === "left" || value === "center" || value === "right";
}

// shape の矢印向き（arrowFromCorner）用の検証。サーバ側 zod（enum の 4 値）と同等の制約を
// クライアント貼り付け経路でも適用し、細工した貼り付けデータで不正値が一時的にローカル状態へ
// 残らないようにする。
function isOptionalArrowCorner(value: unknown): boolean {
  return (
    value === undefined ||
    value === "top-left" ||
    value === "top-right" ||
    value === "bottom-left" ||
    value === "bottom-right"
  );
}

// image/video の placeholderSrc 用の検証。サーバ側 zod スキーマ（apps/api/src/lib/canvas-object-schema.ts
// の PlaceholderSrcSchema）と同等の制約をクライアント貼り付け経路でも適用する。
// クリップボードは外部入力（リポジトリ外の text/plain fallback を含む）であり、サーバ reject 前の
// 楽観更新後に <img src> へ流れて任意 data: URL（特に SVG）を描画候補にしてしまう余地を塞ぐ。
function isOptionalPlaceholderSrc(value: unknown): value is string | undefined {
  return (
    value === undefined ||
    (typeof value === "string" &&
      value.length <= 8192 &&
      /^data:image\/(jpeg|jpg|png|gif|webp);/i.test(value))
  );
}

// image / video の本体 src 用の検証。サーバ側 zod（SafeAttachmentSrcSchema）と同等に、
// http(s) / blob: / ラスタ画像の data: URL のみ許可する。クリップボード（外部入力）から、
// サーバ reject 前の楽観描画で data:image/svg+xml や javascript: などが <img src> へ流れる
// 余地を塞ぎ、placeholderSrc / link-card url と検証方針を揃える。
function isSafeAttachmentSrc(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= 8192 &&
    (/^https?:\/\//i.test(value) ||
      value.startsWith("blob:") ||
      /^data:image\/(jpeg|jpg|png|gif|webp)[;,]/i.test(value))
  );
}

// link-card の url 用のスキーム検証。サーバ側 zod スキーマ（HttpUrlSchema）と同等に http / https
// のみ許可する。クリップボード（text/plain fallback を含む外部入力）から、楽観更新前に
// javascript: / data: 等の危険スキームを持つ link-card を弾く。
// imageUrl は <img src>（非実行）専用で危険スキームでも XSS にならず、サーバ側も緩く受理するため
// ここでも url のみ厳格化し、imageUrl は文字列であれば許容する（描画/サーバ判定の食い違いを避ける）。
function isHttpUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 8192) return false;
  try {
    const protocol = new URL(value).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

function hasBaseFields(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === "string" &&
    isFiniteNumber(record.x) &&
    isFiniteNumber(record.y) &&
    isFiniteNumber(record.w) &&
    isFiniteNumber(record.h) &&
    isOptionalFiniteNumber(record.rotation) &&
    isFiniteNumber(record.zOrder) &&
    isFiniteNumber(record.version) &&
    typeof record.createdAt === "string" &&
    typeof record.createdBy === "string" &&
    typeof record.updatedAt === "string" &&
    typeof record.updatedBy === "string"
  );
}

function isCanvasObject(value: unknown): value is CanvasObject {
  if (!hasBaseFields(value)) return false;
  // クリップボード文字列は外部入力なので、種別ごとの必須フィールドまで
  // 確認してからキャンバスオブジェクトとして扱う。
  switch (value.type) {
    case "sticky":
      return (
        typeof value.text === "string" &&
        isOptionalString(value.color) &&
        // 細工した貼り付けデータで updatedByName にオブジェクトが入り込まないよう型を検証する。
        // sticky-object.tsx ではこの値を React 子要素として直接描画するため、文字列以外が
        // 流入すると貼り付け直後に描画エラー（不正な ReactNode）で画面が壊れる。
        isOptionalString(value.updatedByName) &&
        isOptionalTextAlign(value.textAlign)
      );
    case "text":
      return (
        typeof value.text === "string" &&
        isOptionalString(value.color) &&
        isOptionalFiniteNumber(value.fontSize) &&
        isOptionalTextAlign(value.textAlign)
      );
    case "shape":
      return (
        (value.shapeKind === "rectangle" ||
          value.shapeKind === "circle" ||
          value.shapeKind === "arrow") &&
        isOptionalString(value.fill) &&
        isOptionalString(value.stroke) &&
        isOptionalArrowCorner(value.arrowFromCorner)
      );
    case "image":
      return (
        isSafeAttachmentSrc(value.src) &&
        isOptionalString(value.alt) &&
        isOptionalPlaceholderSrc(value.placeholderSrc)
      );
    case "video":
      return (
        isSafeAttachmentSrc(value.src) &&
        typeof value.mimeType === "string" &&
        isOptionalPlaceholderSrc(value.placeholderSrc)
      );
    case "link-card":
      return (
        isHttpUrl(value.url) &&
        typeof value.title === "string" &&
        isOptionalString(value.description) &&
        isOptionalString(value.imageUrl) &&
        isOptionalString(value.siteName)
      );
    case "pen-stroke":
      return (
        typeof value.color === "string" &&
        isFiniteNumber(value.thickness) &&
        Array.isArray(value.points) &&
        value.points.every(isStrokePoint)
      );
    case "stamp":
      return (
        value.stampKind === "thumbs-up" ||
        value.stampKind === "fire" ||
        value.stampKind === "smile" ||
        value.stampKind === "hundred" ||
        value.stampKind === "plus"
      );
    case "connector":
      return (
        isConnectorEndpoint(value.from) &&
        isConnectorEndpoint(value.to) &&
        isOptionalString(value.stroke)
      );
    default:
      return false;
  }
}

// コネクタ端点（{ objectId, side }）の検証。クリップボード（外部入力）から不正な端点が
// 一時的にローカル状態へ流入しないよう、サーバ zod（ConnectorEndpointSchema）と同等に確認する。
function isConnectorEndpoint(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.objectId === "string" &&
    (record.side === "top" ||
      record.side === "right" ||
      record.side === "bottom" ||
      record.side === "left")
  );
}

function isStrokePoint(value: unknown): value is { x: number; y: number; pressure: number } {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    isFiniteNumber(record.x) &&
    isFiniteNumber(record.y) &&
    isFiniteNumber(record.pressure)
  );
}
